import { createDefaultLogger, LogLevels } from "@/types/console-logger";
import { CacheError, deleteDatabase, runTransaction } from "./cache-database";
import {
    CACHE_OWNER_KEY, DATABASE_NAME, LEGACY_DATABASE_NAMES, STATE_STORE, STORE_NAMES,
    USER_SCOPED_CONTENT_STORES, USER_SCOPED_COOLDOWN_KEYS
} from "./cache-schema";

const logger = createDefaultLogger( LogLevels.DEBUG );

/** A legacy delete blocked by another tab (an old bundle still holding `s`) is skipped, not waited for; next boot retries. */
export const LEGACY_DELETE_TIMEOUT_MS: number = 5_000;

/**
 * Both maintenance entry points drive `runTransaction` directly, so a raw DOMException could escape them.
 * Mirrors `createStore`'s wrapper so every cache failure a consumer sees is a CacheError carrying `cause` (R3b).
 */
async function guard< R >( operation: string, work: () => Promise< R > ): Promise< R > {
    try {
        return await work();
    } catch ( err: unknown ) {
        if ( err instanceof CacheError ) {
            throw err;
        }
        const detail = ( err as { name?: string; message?: string } | null );
        throw new CacheError( `[ cache ] ${ operation } failed: ${ detail?.name ?? 'Error' }${ detail?.message ? ` — ${ detail.message }` : '' }`, { cause: err } );
    }
}

/** Empty every store, including sync bookkeeping, in one transaction. Does NOT touch localStorage: the caller owns that. */
export function resetCachedContent(): Promise< void > {
    return guard( 'resetCachedContent', () => runTransaction( STORE_NAMES, 'readwrite', tx => {
        for ( const name of STORE_NAMES ) {
            tx.objectStore( name ).clear();
        }
    } ) );
}

/**
 * The security boundary when the cache changes hands, as ONE transaction: the outgoing user's
 * entitlement-filtered rows are gone AND the incoming user is recorded, or neither happened.
 */
export function reassignCacheOwner( newOwner: string ): Promise< void > {
    return guard( 'reassignCacheOwner', () => runTransaction( [ ...USER_SCOPED_CONTENT_STORES, STATE_STORE ], 'readwrite', tx => {
        for ( const name of USER_SCOPED_CONTENT_STORES ) {
            tx.objectStore( name ).clear();
        }
        const state = tx.objectStore( STATE_STORE );
        for ( const key of USER_SCOPED_COOLDOWN_KEYS ) {
            state.delete( key );
        }
        state.put( newOwner, CACHE_OWNER_KEY );
    } ) );
}

/**
 * Delete the localforage-era databases. Main thread only; sequential; never on the render path; runs on
 * every boot because the only cost is one `indexedDB.databases()` call and an old-bundle tab can recreate
 * `s` after we delete it (R46). A blocked or failed delete is logged and skipped; the next boot retries.
 */
export async function purgeLegacyDatabases(
    { mainThread = typeof window !== 'undefined' }: { mainThread?: boolean } = {}
): Promise< void > {
    if ( !mainThread ) {
        return;
    }
    if ( typeof indexedDB.databases !== 'function' ) {
        return;   // not reachable on the supported browsers; never delete blind
    }

    const present = new Set( ( await indexedDB.databases() ).map( info => info.name ).filter( ( n ): n is string => !!n ) );
    const targets = LEGACY_DATABASE_NAMES.filter( name => name !== DATABASE_NAME && present.has( name ) );

    for ( const name of targets ) {
        try {
            await deleteDatabase( name, LEGACY_DELETE_TIMEOUT_MS );
            logger.info( `[ cache ] deleted legacy database "${ name }"` );
        } catch ( err: unknown ) {
            logger.warn( `[ cache ] could not delete legacy database "${ name }" (will retry next boot): ${ ( err as Error )?.message ?? err }` );
        }
    }

    // The localforage-era purge kept its flag in localStorage; nothing else will ever remove it (F45).
    if ( typeof localStorage !== 'undefined' ) {
        localStorage.removeItem( 'tmi.legacy-idb-purge' );
    }
}
